import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader, Rocket } from 'lucide-react'
import { Suspense, useMemo, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { COMPANY_LEVEL_VALUE } from '@/components/form/project-select-field'
import { ToasterContent } from '@/components/sv/toaster-content'
import { H3, H4 } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Form } from '@/components/ui/form'
import { Separator } from '@/components/ui/separator'
import {
  useCompanyPath,
  useCreateFormProjectSelection,
  useIdCompany,
  useMixpanel,
  useRBAC,
  useSuspenseQuery,
} from '@/lib/hooks'
import {
  type CreateStaticSiteFormValues,
  schema,
} from '@/routes/static-sites/create-static-site/form/schema'
import { AutoDeploy } from '@/routes/static-sites/create-static-site/form/sections/auto-deploy'
import { BuildSettings } from '@/routes/static-sites/create-static-site/form/sections/build-settings'
import { BuildSettingsFallback } from '@/routes/static-sites/create-static-site/form/sections/build-settings/fallback'
import { EnvVars } from '@/routes/static-sites/create-static-site/form/sections/env-vars'
import { Name } from '@/routes/static-sites/create-static-site/form/sections/name'
import { Overview } from '@/routes/static-sites/create-static-site/form/sections/overview'
import { Project } from '@/routes/static-sites/create-static-site/form/sections/project'
import { PullRequestPreviews } from '@/routes/static-sites/create-static-site/form/sections/pull-request-previews'
import { Source } from '@/routes/static-sites/create-static-site/form/sections/source'
import {
  CREATE_STATIC_SITE_MUTATION,
  CREATE_STATIC_SITE_QUERY,
} from '@/routes/static-sites/create-static-site/gql'
import type {
  CreateStaticSiteMutation,
  CreateStaticSiteMutationVariables,
  CreateStaticSiteQuery,
  CreateStaticSiteQueryVariables,
} from '@/routes/static-sites/create-static-site/gql.types.gen'

export const CreateStaticSiteForm = () => {
  const { idCompany } = useIdCompany()
  const { navigateToPath } = useCompanyPath()
  const { rbac, totalServices, rolesInCompany } = useRBAC()
  const { track } = useMixpanel()
  const [isPending, startTransition] = useTransition()
  const [searchParams] = useSearchParams()

  const idProjectFromUrl = searchParams.get('idProject')

  const { data } = useSuspenseQuery<
    CreateStaticSiteQuery,
    CreateStaticSiteQueryVariables
  >(CREATE_STATIC_SITE_QUERY, {
    variables: { idCompany },
  })

  const [createStaticSite] = useMutation<
    CreateStaticSiteMutation,
    CreateStaticSiteMutationVariables
  >(CREATE_STATIC_SITE_MUTATION)

  const projects = useMemo(() => {
    if (totalServices.totalProjects === 0) {
      return []
    }
    return (data?.company?.projects?.items ?? []).filter((project) =>
      rbac.company(idCompany).project(project.id).staticSites.create.can()
    )
  }, [
    data?.company?.projects?.items,
    idCompany,
    rbac,
    totalServices.totalProjects,
  ])

  const shouldShowProjectField = projects.length > 0
  const hasVerifiedCard = Boolean(data?.company?.defaultPaymentMethod?.id)

  // Company-level users can create static sites at the company level
  const isCompanyLevelUser = rbac.company(idCompany).staticSites.create.can()
  const { initialIdProject, isProjectOptional, schemaWithProjectRequirement } =
    useCreateFormProjectSelection({
      schema,
      rolesInCompany,
      isCompanyLevelUser,
      idProjectFromUrl,
      projects,
    })
  const hasCreateAccess = isCompanyLevelUser || projects.length > 0

  const form = useForm<CreateStaticSiteFormValues>({
    resolver: zodResolver(schemaWithProjectRequirement),
    shouldUnregister: false,
    criteriaMode: 'all',
    defaultValues: {
      idCompany,
      idProject: initialIdProject,
      buildEnabled: true,
      source: 'privateGit',
      gitType: 'github',
      defaultBranch: 'main',
      idRemoteRepository: '',
      repoUrl: '',
      displayName: '',
      buildCommand: 'npm run build',
      installCommand: '',
      indexFile: 'index.html',
      errorFile: '',
      rootDirectory: '.',
      publishedDirectory: '',
      nodeVersion: 'lts',
      autoDeploy: true,
      isPreviewEnabled: false,
      isEnvVarPreview: true,
      isEnvVarProduction: true,
      envVars: [{ key: '', value: '' }],
    },
  })

  const { trigger } = form
  const source = form.watch('source')
  const isCreateDisabled =
    !hasCreateAccess || isPending || form.formState.isSubmitting
  const onSubmit = async (values: CreateStaticSiteFormValues) => {
    const isValid = await trigger()
    if (isPending || !isValid) {
      return
    }
    startTransition(async () => {
      try {
        // Convert COMPANY_LEVEL_VALUE to undefined for API
        const idProject =
          values.idProject === COMPANY_LEVEL_VALUE
            ? undefined
            : values.idProject

        const res = await createStaticSite({
          variables: {
            idCompany,
            idProject,
            source: values.source,
            repoUrl: values.repoUrl ?? '',
            defaultBranch: values.defaultBranch,
            displayName: values.displayName,
            gitType: values.gitType,
            idRemoteRepository: values.idRemoteRepository ?? '',
            buildCommand: values.buildCommand,
            installCommand: values.installCommand,
            rootDirectory: values.rootDirectory,
            publishedDirectory: values.publishedDirectory,
            indexFile: values.indexFile,
            errorFile: values.errorFile,
            nodeVersion: values.nodeVersion,
            autoDeploy: values.autoDeploy,
            isPreviewEnabled: values.isPreviewEnabled,
            envVars:
              values.envVars
                ?.filter((envVar) => Boolean(envVar.key))
                ?.map((envVar) => ({
                  key: envVar.key,
                  value: envVar.value,
                  isProduction: values.isEnvVarProduction ?? true,
                  isPreview: values.isEnvVarPreview ?? false,
                })) ?? [],
          },
        })
        toast(
          <ToasterContent variant="success">
            Static site created successfully
          </ToasterContent>,
          { position: 'top-right' }
        )
        track('Static Site Created', {
          idStaticSite: res.data?.createStaticSite?.id,
          buildType: values.source,
          hasBuildStep: values.buildEnabled,
        })

        navigateToPath({
          pathname: '/static-site/:staticSiteName/overview',
          variables: { staticSiteName: res.data?.createStaticSite?.name ?? '' },
        })
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to create static site
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <Form {...form}>
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-[2fr_1fr] pb-25">
        <div className="flex flex-col">
          <Card>
            <CardContent className="p-0">
              <H3 className="mb-6 px-6">Static site details</H3>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <div className="px-6 space-y-6">
                  <Source hasVerifiedCard={hasVerifiedCard} />
                  <Name />
                  {shouldShowProjectField ? (
                    <Project
                      projects={projects}
                      isAllowedToDeselect={isProjectOptional}
                    />
                  ) : null}
                </div>
                {source === 'privateGit' ? (
                  <>
                    <Separator />
                    <div className="px-6 space-y-6">
                      <H4>Deployment settings</H4>
                      <AutoDeploy />
                      <PullRequestPreviews />
                    </div>
                  </>
                ) : null}
                <Separator />
                <div className="px-6 space-y-6">
                  <Suspense fallback={<BuildSettingsFallback />}>
                    <BuildSettings />
                  </Suspense>
                </div>
                <Separator />
                <div className="px-6 space-y-6">
                  <EnvVars />
                </div>
                <div className="px-6 space-y-6">
                  <Button disabled={isCreateDisabled}>
                    {isPending || form.formState.isSubmitting ? (
                      <Loader className="animate-spin" />
                    ) : (
                      <Rocket />
                    )}
                    Create static site
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
        <div className="flex flex-col gap-4">
          <Overview />
        </div>
      </div>
    </Form>
  )
}
